import { createClient } from '@supabase/supabase-js';

// Get environment variables with fallback to prevent build errors
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Validate at runtime (not during build to allow build to succeed)
if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Supabase environment variables are missing. ' +
    'Please check your .env.local file for REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.'
  );
}

/**
 * Creates a chainable mock query builder that mimics Supabase's PostgrestQueryBuilder.
 * All filtering/modifier methods return `this` so they can be chained.
 * The chain resolves via .then() / await to { data: [], error: null } by default.
 */
function createMockQueryBuilder(operation) {
  const isWrite = ['insert', 'update', 'delete', 'upsert'].includes(operation);
  const resolveValue = isWrite
    ? { data: null, error: { message: 'Supabase not configured' } }
    : { data: [], error: null };

  const builder = {
    // Read modifiers
    select: () => builder,
    // Write operations return chainable builder too
    insert: () => builder,
    update: () => builder,
    delete: () => builder,
    upsert: () => builder,
    // Filter methods
    eq: () => builder,
    neq: () => builder,
    gt: () => builder,
    gte: () => builder,
    lt: () => builder,
    lte: () => builder,
    like: () => builder,
    ilike: () => builder,
    in: () => builder,
    is: () => builder,
    contains: () => builder,
    range: () => builder,
    // Ordering / limiting
    order: () => builder,
    limit: () => builder,
    range: () => builder,
    // Result modifiers
    single: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
    maybeSingle: () => Promise.resolve({ data: null, error: null }),
    // Make the builder thenable so `await query` works
    then: (resolve) => Promise.resolve(resolveValue).then(resolve),
    catch: (reject) => Promise.resolve(resolveValue).catch(reject),
  };

  return builder;
}

let supabase;

if (supabaseUrl && supabaseAnonKey) {
  supabase = createClient(supabaseUrl, supabaseAnonKey);
} else {
  supabase = {
    from: () => createMockQueryBuilder('select'),
    auth: {
      signIn: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
      signInWithPassword: () => Promise.resolve({ data: { user: null, session: null }, error: { message: 'Supabase not configured' } }),
      signUp: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
      signOut: () => Promise.resolve({ error: null }),
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      onAuthStateChange: (callback) => {
        // Mock: hemen INITIAL_SESSION event'i fire et (session yok)
        // Gerçek Supabase client gibi davranması için setTimeout ile asenkron çağır
        setTimeout(() => callback('INITIAL_SESSION', null), 0);
        return { data: { subscription: { unsubscribe: () => {} } } };
      },
    },
    storage: {
      from: () => ({
        upload: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
        download: () => Promise.resolve({ data: null, error: { message: 'Supabase not configured' } }),
        list: () => Promise.resolve({ data: [], error: null }),
        remove: () => Promise.resolve({ data: null, error: null }),
        getPublicUrl: () => ({ data: { publicUrl: '' } }),
      }),
    },
  };
}

export { supabase };
export default supabase;
